<script>
/* global T */
import componentMixin from "../mixins/component-mixin";

export default {
    name: "TdtSlotInfowindow",
    mixins: [componentMixin],
    props: {
        position: {
            type: Array,
            default: () => []
        },
        visible: {
            type: Boolean,
            default: false
        },
        anchor: {
            type: String,
            default: "bottom-center",
            validator(val) {
                return [
                    "top-left",
                    "top-center",
                    "top-right",
                    "middle-left",
                    "center",
                    "middle-right",
                    "bottom-left",
                    "bottom-center",
                    "bottom-right"
                ].includes(val);
            }
        },
        offset: {
            type: Array,
            default: () => [0, 0]
        },
        isCustom: {
            type: Boolean,
            default: false
        },
        closeWhenClickMap: {
            type: Boolean,
            default: false
        },
        autoPan: {
            type: Boolean,
            default: false
        },
        zIndex: {
            type: Number,
            default: 9999
        }
    },
    data() {
        return {
            innerVisible: false
        };
    },
    watch: {
        visible: {
            handler(val) {
                if(val !== this.innerVisible) {
                    this.innerVisible = val;
                    if(this.$tdtComponent) {
                        val ? this.$tdtComponent.show() : this.$tdtComponent.hide();
                    }
                }
            },
            immediate: true
        },
        position: {
            handler(val) {
                if(this.$tdtComponent && val?.length >= 2) {
                    this.$tdtComponent.setLngLat(val);
                }
            },
            deep: true
        },
        offset: {
            handler() {
                if(this.$tdtComponent) {
                    this.$tdtComponent.update();
                }
            },
            deep: true
        },
        anchor() {
            if(this.$tdtComponent) {
                this.$tdtComponent.update();
            }
        }
    },
    render(h) {
        if(this.$slots.default) {
            return h(
                "div",
                {
                    ref: "slotContent"
                },
                this.$slots.default
            );
        }
        return h();
    },
    methods: {
        initComponent() {
            const self = this;
            const lnglat = new T.LngLat(this.position[0], this.position[1]);

            const anchorMap = {
                "top-left": ["left", "bottom"],
                "top-center": ["center", "bottom"],
                "top-right": ["right", "bottom"],
                "middle-left": ["right", "center"],
                "center": ["center", "center"],
                "middle-right": ["left", "center"],
                "bottom-left": ["left", "top"],
                "bottom-center": ["center", "top"],
                "bottom-right": ["right", "top"]
            };

            const CustomOverlay = T.Overlay.extend({
                initialize(lnglat, options) {
                    this.lnglat = lnglat;
                    this.setOptions(options);
                    this._visible = false;
                },

                getType() {
                    return 1;
                },

                onAdd(map) {
                    this._map = map;
                    const div = this._div = document.createElement("div");
                    div.className = self.isCustom ? "tdt-slot-infowindow-custom" : "tdt-slot-infowindow";
                    div.style.position = "absolute";
                    div.style.zIndex = self.zIndex;
                    div.style.pointerEvents = "auto";

                    if(!self.isCustom) {
                        div.style.background = "#fff";
                        div.style.borderRadius = "4px";
                        div.style.boxShadow = "0 2px 12px rgba(0, 0, 0, 0.15)";
                        div.style.padding = "12px";
                    }

                    if(self.$refs.slotContent) {
                        div.appendChild(self.$refs.slotContent);
                        self.$refs.slotContent.style.display = "";
                    }

                    if(self.innerVisible) {
                        div.style.display = "";
                        this._visible = true;
                    }else {
                        div.style.display = "none";
                        this._visible = false;
                    }

                    this._bindEvents();
                    map.getPanes().infoWindowPane.appendChild(div);
                    this.update();
                },

                onRemove() {
                    this._unbindEvents();
                    const parent = this._div?.parentNode;
                    if(parent) {
                        parent.removeChild(this._div);
                    }
                    this._map = null;
                    this._div = null;
                },

                _bindEvents() {
                    if(self.closeWhenClickMap && this._map) {
                        this._onMapClick = () => {
                            self.close();
                        };
                        this._map.addEventListener("click", this._onMapClick);
                    }
                },

                _unbindEvents() {
                    if(self.closeWhenClickMap && this._map && this._onMapClick) {
                        this._map.removeEventListener("click", this._onMapClick);
                    }
                },

                update() {
                    if(!this._map || !this._div || !this.lnglat) return;

                    const pos = this._map.lngLatToLayerPoint(this.lnglat);
                    const [offsetX, offsetY] = self.offset;
                    const [anchorX, anchorY] = anchorMap[self.anchor] || ["center", "top"];

                    let transformX = 0;
                    let transformY = 0;

                    const divWidth = this._div.offsetWidth;
                    const divHeight = this._div.offsetHeight;

                    switch (anchorX) {
                        case "left":
                            transformX = 0;
                            break;
                        case "center":
                            transformX = -divWidth / 2;
                            break;
                        case "right":
                            transformX = -divWidth;
                            break;
                    }

                    switch (anchorY) {
                        case "top":
                            transformY = 0;
                            break;
                        case "center":
                            transformY = -divHeight / 2;
                            break;
                        case "bottom":
                            transformY = -divHeight;
                            break;
                    }

                    this._div.style.left = (pos.x + offsetX + transformX) + "px";
                    this._div.style.top = (pos.y + offsetY + transformY) + "px";

                    if(self.autoPan && this._visible) {
                        this._panIntoView();
                    }
                },

                _panIntoView() {
                    if(!this._map || !this._div) return;

                    const divRect = this._div.getBoundingClientRect();
                    const mapContainer = this._map.getContainer();
                    const mapRect = mapContainer.getBoundingClientRect();

                    const padding = 20;
                    let panX = 0;
                    let panY = 0;

                    if(divRect.left < mapRect.left + padding) {
                        panX = divRect.left - mapRect.left - padding;
                    }else if(divRect.right > mapRect.right - padding) {
                        panX = divRect.right - mapRect.right + padding;
                    }

                    if(divRect.top < mapRect.top + padding) {
                        panY = divRect.top - mapRect.top - padding;
                    }else if(divRect.bottom > mapRect.bottom - padding) {
                        panY = divRect.bottom - mapRect.bottom + padding;
                    }

                    if(panX !== 0 || panY !== 0) {
                        const center = this._map.getCenter();
                        const pixelOffset = this._map.lngLatToLayerPoint(center);
                        const newCenter = this._map.layerPointToLngLat({
                            x: pixelOffset.x - panX,
                            y: pixelOffset.y - panY
                        });
                        this._map.panTo(newCenter);
                    }
                },

                show() {
                    console.log('show');
                    if(this._div) {
                        this._visible = true;
                        this.update();
                    }
                },

                hide() {
                    console.log('hide');
                    if(this._div) {
                        this._visible = false;
                    }
                },

                isHidden() {
                    return !this._visible;
                },

                setLngLat(position) {
                    if(position?.length >= 2) {
                        this.lnglat = new T.LngLat(position[0], position[1]);
                        this.update();
                    }
                },

                getElement() {
                    return this._div;
                }
            });

            this.$tdtComponent = new CustomOverlay(lnglat, {});

            if(this.innerVisible) {
                this.$nextTick(() => {
                    this.$tdtComponent.show();
                });
            }
        },
        close() {
            this.innerVisible = false;
            this.$emit("update:visible", false);
            this.$emit("close");
            if(this.$tdtComponent) {
                this.$tdtComponent.hide();
            }
        },
        open() {
            this.innerVisible = true;
            this.$emit("update:visible", true);
            this.$emit("open");
            if(this.$tdtComponent) {
                this.$tdtComponent.show();
            }
        }
    }
};
</script>

<style>
.tdt-slot-infowindow {
    pointer-events: auto;
}

.tdt-slot-infowindow-custom {
    pointer-events: auto;
}
</style>
